import type { PageContent } from "$lib/def/seo/page";

import { Subscription, map, of, switchMap } from "rxjs";
import { createUpdator, editing$, get, obsIsAdmin, watcher } from "../seo";
import { isPossiblyAdminUser } from "./auth";
import { toCanvas } from 'qrcode';

export type Editor = (currentValue: string) => Promise<string | undefined> | undefined

export function createLinkEditorAction(collection:string, docId:string) {
	const updator = createUpdator(collection, docId);
	const updates$ = watcher(collection, docId);
	const contentPromise = get(collection, docId);
	return (node: HTMLElement, [prop, editor]: [string, Editor]) => {

		let currentValue: string;

		const onActivate = () => {
			node.onclick = (e) => {
				e.preventDefault(); // don't follow the link
				e.stopPropagation(); // don't go to parent's click event
				editor?.(currentValue)?.then(value => typeof value === 'undefined' ? undefined : updator(prop, value));
			}
		}
		const onDeactivate = () => {
			node.onclick = null;
		}
	
		const sub = editing$.subscribe(editing => {
			if (editing) {
				onActivate();
			} else {
				onDeactivate();
			}
		});
	
		let subFirestore: Subscription;
		if (isPossiblyAdminUser()) { // avoid setting up firebase overhead at all cost
			const $ = obsIsAdmin().pipe(
				switchMap(isAdmin => isAdmin ? updates$.pipe(map(([,doc]) => doc?.[prop])) : contentPromise.then(item => item?.[1][prop])),
			)
			subFirestore = $.subscribe(value => {
				node.setAttribute('href', value);
			});
		}
		
		// if not set then do so
		contentPromise.then(item => {
			currentValue = item?.[1][prop];
			const currentHref = node.getAttribute('href');
			if (currentHref !== currentValue) node.setAttribute('href', currentValue);
		});

		return {
			destroy() {
				sub.unsubscribe();
				subFirestore?.unsubscribe();
				onDeactivate();
			}
		};
	}

}

export function qrcode(node: HTMLCanvasElement, [text,lightInk]:[string,boolean?]) {
	const a = '#000000ff';
	const b = '#ffffffff';
	const act = (text:string, darkInk = true) => {
		toCanvas(node, text, { color: { dark: darkInk ? a : b, light: darkInk? b : a } });
	}
	act(text, !lightInk);

	return {
		update([text,darkInk]:[string,boolean]) {
			act(text, darkInk);
		}
	}
}
